import { useState, useRef, useEffect, useCallback } from 'react'
import { useMutation } from '@tanstack/react-query'
import { EVENT_ANALYTICS_TYPE_UPDATED, trackUpdate } from '../../../../utils/matomoEvents'
import Api, { DEFAULT_ERROR_MESSAGE } from '../../../../api/Api'
import { IScatterTableApiPayload } from '../../../../types/chart' // sesuaikan pathnya

export const useScatterTable = (coordinates: number[]) => {
  const [scatterTableData, setScatterTableData] = useState<any[]>([])
  const [scatterTableLinks, setScatterTableLinks] = useState<string[]>([])
  const [scatterTableError, setScatterTableError] = useState('')

  const abortControllerRef = useRef<AbortController | null>(null)

  const scatterTableMutation = useMutation({
    mutationFn: async (payload: IScatterTableApiPayload & { signal?: AbortSignal }) => {
      const { signal, ...restPayload } = payload

      const result = await Api.chart.getScatterTable(restPayload, { signal })

      return { result, payload: restPayload }
    },
    onSuccess: ({ result, payload }) => {
      if (result?.Data) {
        setScatterTableData(result.Data)
        setScatterTableLinks(result.DownloadLinks || [])
        setScatterTableError('')
        trackUpdate(EVENT_ANALYTICS_TYPE_UPDATED, coordinates, payload.DatasetId, 'scatter table')
      } else {
        const isCmems = payload.DatasetId.toLowerCase().includes('cmems') || payload.DatasetId2?.toLowerCase().includes('cmems')
        const errorMessage = isCmems ? 'Cmems error: Layer not available' : DEFAULT_ERROR_MESSAGE
        
        setScatterTableError(errorMessage)
        setScatterTableData([])
        setScatterTableLinks([])
      }
    },
    onError: (error: any, variables) => {
      if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') {
        setScatterTableData([])
        setScatterTableLinks([])
        return
      } 
      const isCmems = variables?.DatasetId?.toLowerCase().includes('cmems') ||  variables?.DatasetId2?.toLowerCase().includes('cmems')
      const errorMessage = isCmems ? 'Cmems error: Layer not available' : DEFAULT_ERROR_MESSAGE
      setScatterTableError(errorMessage)
      setScatterTableData([])
      setScatterTableLinks([])
    },
  })

  const updateScatterTable = useCallback((payload: IScatterTableApiPayload) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    const controller = new AbortController()
    abortControllerRef.current = controller

    scatterTableMutation.mutate({
      ...payload,
      signal: controller.signal,
    })
  },[scatterTableMutation])

  const cancelScatterTable = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
  }, [])

  const resetScatterTable = () => {
    setScatterTableData([])
    setScatterTableLinks([])
    setScatterTableError('')
  }

  return {
    scatterTableData,
    scatterTableLinks,
    scatterTableError,
    resetScatterTable,
    updateScatterTable,
    cancelScatterTable,
    isUpdatingScatterTable: scatterTableMutation.isPending,
  }
}
